import { apiParams, readDir } from "@/lib/list-url";
import { exportStaffCompanies } from "@/lib/staff-api";
import { staffCan } from "@/lib/staff-capabilities";
import { companiesCsv, csvFilename } from "@/lib/staff-export";
import { useStaffCompanies } from "@/lib/staff-hooks";
import { useStaffAuth } from "@/lib/use-staff-auth";
import { CompaniesTable } from "./CompaniesTable";
import { DownloadCsvButton } from "./DownloadCsvButton";
import { Loaded } from "./Loaded";
import { UrlTickbox } from "./ListControls";
import { Pager } from "./Pager";
import { useListUrl, usePageCorrection, useSortHandler } from "./use-list-url";

/** Every company, as a sortable table. No in-tab filter: the header search covers it. */
export function StaffCompaniesPage(): React.ReactElement {
  const { staff } = useStaffAuth();
  const canSeeHidden = staff !== null && staffCan(staff.role, "manage_staff");
  const [url, change] = useListUrl();
  const params = apiParams(url, canSeeHidden);
  const query = useStaffCompanies(params);
  usePageCorrection("page", query.isPlaceholderData ? undefined : query.data?.page);
  const onSort = useSortHandler();
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {canSeeHidden && <UrlTickbox label="Show hidden companies" param="includeHidden" />}
        <DownloadCsvButton build={async () => companiesCsv(await exportStaffCompanies(params))} filename={() => csvFilename("companies")} />
      </div>
      <Loaded queries={[query]}>
        {() => {
          const data = query.data!;
          return data.total === 0 ? (
            <p className="text-sm text-gray-600">No companies to show.</p>
          ) : (
            <div className="space-y-3">
              <CompaniesTable caption="Companies" rows={data.rows} sort={url.get("sort") ?? "name"} dir={readDir(url)} onSort={onSort} />
              <Pager
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPage={(p) => change({ page: p === 1 ? null : String(p) })}
                label="Companies"
              />
            </div>
          );
        }}
      </Loaded>
    </div>
  );
}
